import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import { 
  AlertDialog, 
  AlertDialogAction, 
  AlertDialogCancel, 
  AlertDialogContent, 
  AlertDialogDescription, 
  AlertDialogFooter, 
  AlertDialogHeader, 
  AlertDialogTitle,
  AlertDialogTrigger
} from "@/components/ui/alert-dialog";
import { ArrowLeft, Pencil, Trash2, Printer, FileText, Award, UserCheck } from "lucide-react";
import { Student } from "@/services/db/types";
import { format } from "date-fns";
import { 
  printStudentProfileAsJPG, 
  printStudentProfileAsPDF 
} from '@/utils/printing/studentProfilePrinter';
import { 
  printStudentProgramDetailsAsJPG, 
  printStudentProgramDetailsAsPDF 
} from '@/utils/printing/studentProgramPrinter';
import { 
  printStudentEnrollmentAsJPG, 
  printStudentEnrollmentAsPDF 
} from '@/utils/printing/studentEnrollmentPrinter';
import { 
  printStudentCertificateAsJPG, 
  printStudentCertificateAsPDF 
} from '@/utils/printing/studentCertificatePrinter';
import StudentProfileFormatDialog from '../students/StudentProfileFormatDialog';
import StudentProgramsPageSelectionDialog from '../students/StudentProgramsPageSelectionDialog';
import StudentEnrollmentPageSelectionDialog from '../students/StudentEnrollmentPageSelectionDialog';
import StudentCertificatesPageSelectionDialog from '../students/StudentCertificatesPageSelectionDialog';

export interface StudentHeaderProps {
  student: Student;
  onDelete: () => Promise<void>;
}

const StudentHeader = ({ student, onDelete }: StudentHeaderProps) => {
  const navigate = useNavigate();
  const [showFormatDialog, setShowFormatDialog] = useState(false);
  const [showProgramsPageDialog, setShowProgramsPageDialog] = useState(false);
  const [showEnrollmentPageDialog, setShowEnrollmentPageDialog] = useState(false);
  const [showCertificatesPageDialog, setShowCertificatesPageDialog] = useState(false);
  const [currentPrintType, setCurrentPrintType] = useState<'profile' | 'programs' | 'enrollment' | 'certificate'>('profile');
  const [selectedPages, setSelectedPages] = useState<{ start: number; end: number } | null>(null);
  const [selectedCertificatePrograms, setSelectedCertificatePrograms] = useState<string[]>([]);

  const handlePrintProfile = () => {
    setCurrentPrintType('profile');
    setShowFormatDialog(true);
  };

  const handlePrintProgramDetails = () => {
    setCurrentPrintType('programs');
    const totalPrograms = student.programs?.length || 0;
    
    if (totalPrograms > 2) {
      setShowProgramsPageDialog(true);
    } else {
      setSelectedPages({ start: 1, end: 1 });
      setShowFormatDialog(true);
    }
  };

  const handlePrintEnrollment = () => {
    setCurrentPrintType('enrollment');
    const totalPrograms = student.programs?.length || 0;
    
    if (totalPrograms > 2) {
      setShowEnrollmentPageDialog(true);
    } else {
      setSelectedPages({ start: 1, end: 1 });
      setShowFormatDialog(true);
    }
  };

  const handleIssueCertificate = () => {
    setCurrentPrintType('certificate');
    const totalPrograms = student.programs?.length || 0;
    
    if (totalPrograms > 1) {
      setShowCertificatesPageDialog(true);
    } else {
      setSelectedCertificatePrograms(student.programs?.map(p => p.title) || []);
      setShowFormatDialog(true);
    }
  };

  const handleProgramsPageSelection = (startPage: number, endPage: number) => {
    setSelectedPages({ start: startPage, end: endPage });
    setShowProgramsPageDialog(false);
    setShowFormatDialog(true);
  };

  const handleEnrollmentPageSelection = (startPage: number, endPage: number) => {
    setSelectedPages({ start: startPage, end: endPage });
    setShowEnrollmentPageDialog(false);
    setShowFormatDialog(true);
  };

  const handleCertificatesSelection = (selectedPrograms: string[]) => {
    setSelectedCertificatePrograms(selectedPrograms);
    setShowCertificatesPageDialog(false);
    setShowFormatDialog(true);
  };

  const handleFormatDownload = async (format: 'jpg' | 'pdf', instructorName?: string, directorName?: string, grade?: string, percentage?: string) => {
    try {
      console.log('StudentHeader - handleFormatDownload called with:', { format, instructorName, directorName, grade, percentage, currentPrintType });
      
      switch (currentPrintType) {
        case 'profile':
          if (format === 'jpg') {
            await printStudentProfileAsJPG(student);
          } else {
            await printStudentProfileAsPDF(student);
          }
          break;
        case 'programs':
          const startPage = selectedPages?.start || 1;
          const endPage = selectedPages?.end || 1;
          if (format === 'jpg') {
            await printStudentProgramDetailsAsJPG(student, startPage, endPage);
          } else {
            await printStudentProgramDetailsAsPDF(student, startPage, endPage);
          }
          break;
        case 'enrollment':
          const enrollStartPage = selectedPages?.start || 1;
          const enrollEndPage = selectedPages?.end || 1;
          if (format === 'jpg') {
            await printStudentEnrollmentAsJPG(student);
          } else {
            await printStudentEnrollmentAsPDF(student);
          }
          break;
        case 'certificate':
          console.log('Certificate case - Processing programs:', selectedCertificatePrograms);
          // Print certificates for selected programs
          for (const programTitle of selectedCertificatePrograms) {
            const program = student.programs?.find(p => p.title === programTitle);
            if (program) {
              console.log('Printing certificate for program:', programTitle, 'with grade:', grade, 'percentage:', percentage);
              if (format === 'jpg') {
                await printStudentCertificateAsJPG(student, program.title, instructorName, directorName, grade, percentage);
              } else {
                await printStudentCertificateAsPDF(student, program.title, instructorName, directorName, grade, percentage);
              }
            }
          }
          break;
      }
    } catch (error) {
      console.error('Error downloading student document:', error);
    } finally {
      setSelectedPages(null);
      setSelectedCertificatePrograms([]);
    }
  };

  // Get full student name including surname if available
  const fullName = student.surname ? `${student.name} ${student.surname}` : student.name;

  return (
    <>
      <div className="mb-6">
        <Button 
          variant="outline" 
          onClick={() => navigate("/students")}
          className="mb-4"
          size="sm"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Students
        </Button>
        
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h2 className="text-3xl font-bold tracking-tight text-nalc-dark dark:text-white">
              {fullName}
            </h2>
            <p className="text-muted-foreground mt-1">
              Student ID: {student.id}
              {student.createdAt && (
                <span className="ml-2">· Added on {format(new Date(student.createdAt), "PPP")}</span>
              )}
            </p>
          </div>
          
          <div className="flex flex-wrap gap-2">
            <Button 
              variant="outline"
              onClick={handlePrintProfile}
              size="sm"
              className="flex-shrink-0"
            >
              <Printer className="mr-2 h-4 w-4" />
              <span className="whitespace-nowrap">Profile</span>
            </Button>

            <Button 
              variant="outline"
              onClick={handlePrintProgramDetails}
              size="sm"
              className="flex-shrink-0"
            >
              <FileText className="mr-2 h-4 w-4" />
              <span className="whitespace-nowrap">Programs</span>
            </Button>

            <Button 
              variant="outline"
              onClick={handlePrintEnrollment}
              size="sm"
              className="flex-shrink-0"
            >
              <UserCheck className="mr-2 h-4 w-4" />
              <span className="whitespace-nowrap">Enrollment</span>
            </Button>

            <Button 
              variant="outline"
              onClick={handleIssueCertificate}
              size="sm"
              className="flex-shrink-0"
            >
              <Award className="mr-2 h-4 w-4" />
              <span className="whitespace-nowrap">Certificate</span>
            </Button>

            <Button 
              variant="outline" 
              onClick={() => navigate(`/students/edit/${student.id}`)}
              size="sm"
              className="flex-shrink-0"
            >
              <Pencil className="mr-2 h-4 w-4" />
              <span className="whitespace-nowrap">Edit</span>
            </Button>
            
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button 
                  variant="destructive"
                  size="sm"
                  className="flex-shrink-0"
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  <span className="whitespace-nowrap">Delete</span>
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This action will permanently delete the student and all their program data.
                    This action cannot be undone.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction 
                    onClick={onDelete}
                    className="bg-red-600 hover:bg-red-700"
                  >
                    Delete
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>
      </div>

      {/* Format Selection Dialog */}
      <StudentProfileFormatDialog
        isOpen={showFormatDialog}
        onClose={() => {
          setShowFormatDialog(false);
          setSelectedPages(null);
          setSelectedCertificatePrograms([]);
        }}
        onDownload={handleFormatDownload}
        profileType={currentPrintType}
        studentName={fullName}
      />

      {/* Programs Page Selection Dialog */}
      <StudentProgramsPageSelectionDialog
        isOpen={showProgramsPageDialog}
        onClose={() => setShowProgramsPageDialog(false)}
        totalPrograms={student.programs?.length || 0}
        onDownload={handleProgramsPageSelection}
        studentName={fullName}
      />

      {/* Enrollment Page Selection Dialog */}
      <StudentEnrollmentPageSelectionDialog
        isOpen={showEnrollmentPageDialog}
        onClose={() => setShowEnrollmentPageDialog(false)}
        totalPrograms={student.programs?.length || 0}
        onDownload={handleEnrollmentPageSelection}
        studentName={fullName}
      />

      {/* Certificates Selection Dialog */}
      <StudentCertificatesPageSelectionDialog
        isOpen={showCertificatesPageDialog}
        onClose={() => setShowCertificatesPageDialog(false)}
        student={student}
        onDownload={handleCertificatesSelection}
      />
    </>
  );
};

export default StudentHeader;
